import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { useAppData } from "./store";
import { getCategories } from "./categories";
import { Upload, X, FileText, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = {
  "01": "Janeiro", "02": "Fevereiro", "03": "Março", "04": "Abril",
  "05": "Maio",    "06": "Junho",     "07": "Julho", "08": "Agosto",
  "09": "Setembro","10": "Outubro",   "11": "Novembro","12": "Dezembro",
};

// ─── Client-side parsers ──────────────────────────────────────────────────────

const parseCSV = (text) => {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  const out = [];
  for (const line of lines) {
    const parts = line.split(/[;,\t]/).map((p) => p.trim());
    if (parts.length < 3) continue;
    const [dataRaw, desc, valorRaw, , parcelas] = parts;
    let data = dataRaw;
    const m = dataRaw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) data = `${m[3]}-${m[2]}-${m[1]}`;
    const valor = Number(String(valorRaw).replace(/[R$\s.]/g, "").replace(",", "."));
    if (!desc || !valor || valor <= 0) continue;
    out.push({ data, desc, valor, cat: "eventuais", parcelas: parcelas || "1/1" });
  }
  return out;
};

const parseOFX = (text) => {
  const out = [];
  // Match both SGML (<TAG>value) and XML (<TAG>value</TAG>) forms
  const blocks = text.match(/<STMTTRN[\s\S]*?(?:<\/STMTTRN>|(?=<STMTTRN|$))/gi) || [];
  const get = (block, tag) => {
    const rx = new RegExp(`<${tag}>([^<\\r\\n]+)`, "i");
    const m = rx.exec(block);
    return m ? m[1].trim() : "";
  };
  for (const block of blocks) {
    const trntype = get(block, "TRNTYPE").toUpperCase();
    // Only debits (expenses): DEBIT, PAYMENT; skip credits/deposits
    if (trntype === "CREDIT" || trntype === "DEP") continue;
    const dtposted = get(block, "DTPOSTED");
    const trnamt  = get(block, "TRNAMT");
    const name    = get(block, "NAME") || get(block, "MEMO");
    if (!name || !trnamt) continue;
    const amt = parseFloat(trnamt.replace(",", "."));
    if (isNaN(amt) || amt === 0) continue;
    // OFX dates: YYYYMMDD[HHMMSS[.mmm][TZO]]
    const dateStr = dtposted.length >= 8
      ? `${dtposted.slice(0, 4)}-${dtposted.slice(4, 6)}-${dtposted.slice(6, 8)}`
      : new Date().toISOString().slice(0, 10);
    out.push({ data: dateStr, desc: name, valor: Math.abs(amt), cat: "eventuais", parcelas: "1/1" });
  }
  return out;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ImportPdfModal = ({ open, onOpenChange, cartaoId }) => {
  const { data, importCartaoCSV } = useAppData();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [meta, setMeta] = useState({ pages: 0, raw_chars: 0, total_fatura: 0, vencimento_mes: "", format: "" });

  const reset = () => {
    setFile(null);
    setPreview([]);
    setMeta({ pages: 0, raw_chars: 0, total_fatura: 0, vencimento_mes: "", format: "" });
  };

  const updateRow = (i, patch) =>
    setPreview((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeRow = (i) => setPreview((p) => p.filter((_, idx) => idx !== i));

  const totalExtraido = preview.reduce((s, t) => s + (Number(t.valor) || 0), 0);
  const diff   = meta.total_fatura > 0 ? Math.abs(totalExtraido - meta.total_fatura) : 0;
  const diffOk = meta.total_fatura > 0 ? diff < 5 : true;

  const vencMes  = meta.vencimento_mes;
  const vencLabel = vencMes
    ? `${MONTH_NAMES[vencMes.slice(5, 7)] || vencMes.slice(5, 7)}/${vencMes.slice(0, 4)}`
    : "";

  const parceladosCount = preview.filter((t) => {
    const m = String(t.parcelas || "1/1").match(/^\d+\/(\d+)$/);
    return m && parseInt(m[1], 10) > 1;
  }).length;

  // ── file handler ─────────────────────────────────────────────────────────
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().split(".").pop();

    setFile(f);
    setPreview([]);

    // ── CSV / TXT ─────────────────────────────────────────────────────────
    if (ext === "csv" || ext === "txt") {
      try {
        const text = await f.text();
        const items = parseCSV(text).map((t) => ({
          ...t,
          cat: getCategories(data?.customCategories).find((c) => c.id === t.cat)?.id || "eventuais",
        }));
        setPreview(items);
        setMeta({ pages: 1, raw_chars: text.length, total_fatura: 0, vencimento_mes: "", format: "CSV" });
        if (items.length === 0) toast.warning("Nenhuma transação encontrada no arquivo.");
        else toast.success(`${items.length} transações encontradas no CSV`);
      } catch {
        toast.error("Falha ao ler o arquivo CSV.");
      }
      return;
    }

    // ── OFX / OFC ────────────────────────────────────────────────────────
    if (ext === "ofx" || ext === "ofc") {
      try {
        const text = await f.text();
        const items = parseOFX(text).map((t) => ({
          ...t,
          cat: getCategories(data?.customCategories).find((c) => c.id === t.cat)?.id || "eventuais",
        }));
        setPreview(items);
        setMeta({ pages: 1, raw_chars: text.length, total_fatura: 0, vencimento_mes: "", format: "OFX" });
        if (items.length === 0) toast.warning("Nenhuma transação de débito encontrada no arquivo OFX.");
        else toast.success(`${items.length} transações encontradas no OFX`);
      } catch {
        toast.error("Falha ao ler o arquivo OFX.");
      }
      return;
    }

    // ── XLS / XLSX ────────────────────────────────────────────────────────
    if (ext === "xls" || ext === "xlsx") {
      toast.error("XLS/XLSX: abra no Excel ou Google Sheets → Arquivo → Fazer download como CSV, e importe o CSV aqui.");
      setFile(null);
      return;
    }

    // ── PDF or Image → backend AI ─────────────────────────────────────────
    const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext);
    const isPdf   = ext === "pdf";

    if (!isPdf && !isImage) {
      toast.error("Formato não suportado. Use PDF, OFX, CSV ou imagem (JPG/PNG/WEBP).");
      setFile(null);
      return;
    }

    const MAX_MB = 30;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo muito grande (${(f.size / 1024 / 1024).toFixed(1)} MB) — máx ${MAX_MB} MB`);
      setFile(null);
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/cartao/import-pdf`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const items = (r.data.transacoes || []).map((t) => ({
        ...t,
        cat: getCategories(data?.customCategories).find((c) => c.id === t.cat)?.id || "eventuais",
      }));
      setPreview(items);
      setMeta({
        pages: r.data.pages || 1,
        raw_chars: r.data.raw_chars || 0,
        total_fatura: r.data.total_fatura || 0,
        vencimento_mes: r.data.vencimento_mes || "",
        format: isImage ? "Imagem" : "PDF",
      });
      if (items.length === 0) toast.warning("Nenhuma transação detectada.");
      else toast.success(`✨ IA extraiu ${items.length} transações${r.data.pages > 1 ? ` de ${r.data.pages} páginas` : ""}`);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "Tempo esgotado — arquivo muito grande. Tente um arquivo menor." : "Falha ao processar o arquivo");
      toast.error(msg);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  // ── confirm import ────────────────────────────────────────────────────────
  const confirm = () => {
    if (!preview.length) return toast.error("Nada para importar");
    if (meta.total_fatura > 0 && !diffOk) {
      const go = window.confirm(
        `⚠️ Atenção: a soma das transações (R$ ${totalExtraido.toFixed(2)}) difere do total da fatura (R$ ${meta.total_fatura.toFixed(2)}) em R$ ${diff.toFixed(2)}.\n\nDeseja importar mesmo assim?`
      );
      if (!go) return;
    }
    const count = importCartaoCSV(cartaoId, preview);
    onOpenChange(false);
    setTimeout(() => {
      reset();
      toast.success(
        `${count} transação(ões) importada(s)!` +
        (parceladosCount > 0 ? ` (${parceladosCount} parceladas distribuídas automaticamente)` : "")
      );
    }, 200);
  };

  const closeAndReset = () => {
    onOpenChange(false);
    setTimeout(() => reset(), 200);
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); else onOpenChange(o); }}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pdf-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Importar Fatura</DialogTitle>
          <DialogDescription>
            Aceita <b>PDF</b>, <b>OFX/OFC</b>, <b>CSV</b> e <b>imagem</b> (JPG, PNG, WEBP).
            PDF e imagens são analisados pela IA automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          <label className="flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-2xl border-2 border-dashed border-slate-700 hover:border-emerald-500/40 transition cursor-pointer text-center">
            <Upload className="h-8 w-8 text-slate-400" />
            <div className="text-sm font-semibold text-slate-200">
              Clique para escolher o arquivo da fatura
            </div>
            <div className="text-xs text-slate-500">PDF · OFX · CSV · JPG / PNG / WEBP (máx 30 MB)</div>
            <input
              type="file"
              accept="application/pdf,.pdf,.ofx,.ofc,.csv,.txt,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={handleFile}
              className="hidden"
              data-testid="pdf-input"
            />
          </label>

          {/* File info */}
          {file && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900 border border-slate-800">
              <FileText className="h-5 w-5 text-emerald-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{file.name}</div>
                <div className="text-[11px] text-slate-400">
                  {(file.size / 1024).toFixed(1)} KB
                  {meta.pages > 1 ? ` · ${meta.pages} páginas` : ""}
                  {meta.raw_chars > 0 ? ` · ${meta.raw_chars} caracteres` : ""}
                  {meta.format ? ` · Formato: ${meta.format}` : ""}
                </div>
              </div>
              {loading && <Loader2 className="h-4 w-4 animate-spin text-emerald-300 shrink-0" />}
            </div>
          )}

          {/* Invoice metadata */}
          {preview.length > 0 && (meta.total_fatura > 0 || vencLabel) && (
            <div className="grid grid-cols-2 gap-3">
              {meta.total_fatura > 0 && (
                <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">
                    Total desta fatura
                  </div>
                  <div className="text-lg font-bold text-emerald-300 tabular-nums">
                    R$ {meta.total_fatura.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              {vencLabel && (
                <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">
                    Mês de vencimento
                  </div>
                  <div className="text-lg font-bold text-blue-300">{vencLabel}</div>
                </div>
              )}
            </div>
          )}

          {/* Divergence warning */}
          {preview.length > 0 && meta.total_fatura > 0 && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
              diffOk
                ? "bg-emerald-500/10 border-emerald-500/20"
                : "bg-amber-500/10 border-amber-500/20"
            }`}>
              {diffOk
                ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
              }
              <span className="text-xs">
                {diffOk
                  ? `✅ Soma das transações (R$ ${totalExtraido.toFixed(2)}) confere com o total da fatura`
                  : `⚠️ Soma (R$ ${totalExtraido.toFixed(2)}) difere do total da fatura (R$ ${meta.total_fatura.toFixed(2)}) em R$ ${diff.toFixed(2)} — você será alertado antes de salvar`
                }
              </span>
            </div>
          )}

          {/* Parcel info */}
          {parceladosCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-500/10 border-blue-500/20">
              <span className="text-xs text-blue-300">
                📦 {parceladosCount} compra(s) parcelada(s) — as parcelas futuras serão distribuídas automaticamente nos próximos meses
              </span>
            </div>
          )}

          {/* Preview table */}
          {preview.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between gap-3 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-800">
                <span>Revisão — {preview.length} transações</span>
                <span className="text-emerald-300 normal-case font-bold tabular-nums">
                  Soma: R$ {totalExtraido.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-800">
                {preview.map((tx, i) => (
                  <div key={`row-${i}`} className="grid grid-cols-12 gap-2 px-4 py-2 items-center text-xs hover:bg-slate-900/80">
                    <input
                      type="date"
                      value={tx.data}
                      onChange={(e) => updateRow(i, { data: e.target.value })}
                      className="col-span-2 h-8 px-2 rounded bg-slate-900 border border-slate-700 text-slate-200"
                    />
                    <input
                      type="text"
                      value={tx.desc}
                      onChange={(e) => updateRow(i, { desc: e.target.value })}
                      className="col-span-4 h-8 px-2 rounded bg-slate-900 border border-slate-700 text-white"
                    />
                    <select
                      value={tx.cat}
                      onChange={(e) => updateRow(i, { cat: e.target.value })}
                      className="col-span-2 h-8 px-2 rounded bg-slate-900 border border-slate-700 text-slate-200"
                    >
                      {getCategories(data?.customCategories).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={tx.parcelas}
                      onChange={(e) => updateRow(i, { parcelas: e.target.value })}
                      placeholder="1/1"
                      className="col-span-1 h-8 px-2 rounded bg-slate-900 border border-slate-700 text-center text-slate-300"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={tx.valor}
                      onChange={(e) => updateRow(i, { valor: Number(e.target.value) })}
                      className="col-span-2 h-8 px-2 rounded bg-slate-900 border border-slate-700 text-right text-emerald-300 font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="col-span-1 h-7 w-7 mx-auto grid place-items-center rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-300"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={closeAndReset} className="flex-1">
              <X className="h-4 w-4 mr-1.5" /> Fechar
            </Button>
            <Button
              type="button"
              onClick={confirm}
              disabled={!preview.length || loading}
              className="flex-1 monyx-btn-primary text-white"
              data-testid="pdf-import"
            >
              Importar {preview.length > 0 && `(${preview.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
